import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listProgramShifts,
  listStaffRoles,
  listPeople,
  generateShifts,
  autoAssignStaff,
  confirmAssignments,
  reopenShiftAssignment,
  reopenAssignments,
  clearProgramAssignments,
  deleteProgramShifts,
  assignPersonToShift,
  unassignShift,
  updateProgram,
  createShift,
  deleteShift,
  updateShift,
  ensureProgramTimetableToken,
  type Place,
  type Happening,
  type Program,
  type StaffShiftWithAssignment,
} from '@littlebooker/dal';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Wand2, CheckCheck, Lock, Unlock, AlertTriangle, Eraser, Trash2, Plus, RefreshCw, Pin, Link2, Eye, EyeOff, Copy } from 'lucide-react';

interface ProgramStaffingViewProps {
  program: Program;
  stages: Place[];
  happenings: Happening[];
  programStart: Date;
  programEnd: Date;
}

const DEFAULT_MIN_MINUTES = 180;
const DEFAULT_MAX_MINUTES = 300;

const SLOT_MINUTES = 30;
const SLOT_SIZE_PX = 60; // per 30 min
const TRACK_HEIGHT = 36;
const STAGE_HEADER_HEIGHT = 36;
const LABEL_WIDTH = 200;

const slotCount = (24 * 60) / SLOT_MINUTES;

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function fmtTime(d: Date): string {
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

// Pack time-overlapping items into stacked sub-lanes (interval graph colouring):
// each item gets the lowest lane index free at its start. Returns the lane per
// item and the total lane count.
function packLanes<T extends { start_time: string | null; end_time: string | null }>(
  items: T[],
): { lanes: Map<T, number>; count: number } {
  const startOf = (it: T) => Date.parse(it.start_time ?? '') || 0;
  const endOf = (it: T) => Date.parse(it.end_time ?? it.start_time ?? '') || startOf(it);
  const sorted = [...items].sort((a, b) => startOf(a) - startOf(b));
  const laneEnds: number[] = [];
  const lanes = new Map<T, number>();
  for (const it of sorted) {
    const start = startOf(it);
    const end = endOf(it);
    let lane = laneEnds.findIndex((e) => e <= start);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(end);
    } else {
      laneEnds[lane] = end;
    }
    lanes.set(it, lane);
  }
  return { lanes, count: Math.max(1, laneEnds.length) };
}

export function ProgramStaffingView({ program, stages, happenings, programStart, programEnd }: ProgramStaffingViewProps) {
  const programId = program.id;
  const queryClient = useQueryClient();

  // Shift length band (hours), persisted on the program. The engine balances each
  // coverage run into shifts whose length stays within [min, max], cutting on
  // event boundaries; the average lands near the middle of the band.
  const savedMin = Number((program.settings as { staffing_min_minutes?: number } | null)?.staffing_min_minutes);
  const [minHours, setMinHours] = useState<string>(
    String((Number.isFinite(savedMin) && savedMin > 0 ? savedMin : DEFAULT_MIN_MINUTES) / 60),
  );
  const minMinutes = Math.max(15, Math.round((Number(minHours) || 3) * 60));

  const savedMax = Number((program.settings as { staffing_max_minutes?: number } | null)?.staffing_max_minutes);
  const [maxHours, setMaxHours] = useState<string>(
    String((Number.isFinite(savedMax) && savedMax > 0 ? savedMax : DEFAULT_MAX_MINUTES) / 60),
  );
  const maxMinutes = Math.max(minMinutes, Math.round((Number(maxHours) || 5) * 60));

  // Max shifts a single person may be assigned (program-level). Empty = no cap.
  const savedMaxShifts = Number((program.settings as { staffing_max_shifts_per_person?: number } | null)?.staffing_max_shifts_per_person);
  const [maxShiftsInput, setMaxShiftsInput] = useState<string>(
    Number.isFinite(savedMaxShifts) && savedMaxShifts > 0 ? String(savedMaxShifts) : '',
  );
  const maxShiftsPerPerson = maxShiftsInput.trim() ? Math.max(1, Math.round(Number(maxShiftsInput))) : undefined;

  // Lead-in / lead-out: extra coverage (minutes) before the first / after the
  // last act of a run (call-time, teardown). Persisted on the program.
  const settings = (program.settings as { staffing_leadin_minutes?: number; staffing_leadout_minutes?: number } | null) ?? {};
  const [leadInInput, setLeadInInput] = useState<string>(
    Number.isFinite(Number(settings.staffing_leadin_minutes)) ? String(settings.staffing_leadin_minutes) : '',
  );
  const [leadOutInput, setLeadOutInput] = useState<string>(
    Number.isFinite(Number(settings.staffing_leadout_minutes)) ? String(settings.staffing_leadout_minutes) : '',
  );
  const leadInMinutes = Math.max(0, Math.round(Number(leadInInput) || 0));
  const leadOutMinutes = Math.max(0, Math.round(Number(leadOutInput) || 0));

  // Days spanned by the program.
  const days = useMemo(() => {
    const out: Date[] = [];
    const cur = startOfDay(programStart);
    const last = startOfDay(programEnd);
    while (cur <= last) {
      out.push(new Date(cur));
      cur.setDate(cur.getDate() + 1);
    }
    return out.length > 0 ? out : [startOfDay(programStart)];
  }, [programStart, programEnd]);

  const [activeDayIdx, setActiveDayIdx] = useState(0);
  const activeDay = days[Math.min(activeDayIdx, days.length - 1)];

  // Continuous timeline spanning the whole programme (all days concatenated into
  // one scrollable axis) so shifts and drags can cross midnight. Day pills scroll
  // to a day rather than switching the view.
  const timelineStart = startOfDay(days[0] ?? programStart).getTime();
  const dayCount = days.length;
  const totalMinutes = dayCount * 24 * 60;
  const totalSlots = dayCount * slotCount;
  // The horizontal scroll container, so day pills can jump to a day's offset.
  const gridScrollRef = useRef<HTMLDivElement | null>(null);
  const dayWidthPx = slotCount * SLOT_SIZE_PX;
  const scrollToDay = (idx: number) => {
    setActiveDayIdx(idx);
    gridScrollRef.current?.scrollTo({ left: idx * dayWidthPx, behavior: 'smooth' });
  };
  // Keep the active pill in sync with where the timeline is scrolled.
  const onGridScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const idx = Math.round(e.currentTarget.scrollLeft / dayWidthPx);
    const clamped = Math.max(0, Math.min(dayCount - 1, idx));
    setActiveDayIdx((cur) => (cur === clamped ? cur : clamped));
  };

  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [personFilter, setPersonFilter] = useState<string>('all');
  const [problemsOnly, setProblemsOnly] = useState(false);
  // Manual build: show a draggable lane for every role on every stage, so shifts
  // can be hand-created without first running Auto-assign.
  const [manualBuild, setManualBuild] = useState(false);
  // Stages with no shifts have no draggable lane. Let the user reveal a specific
  // role's lane per stage (without turning on full manual build) so they can drag
  // a first shift. Keyed by stage id → role ids to show.
  const [revealedLanes, setRevealedLanes] = useState<Record<string, string[]>>({});
  const revealLane = (stageId: string, roleId: string) =>
    setRevealedLanes((cur) => {
      const existing = cur[stageId] ?? [];
      return existing.includes(roleId) ? cur : { ...cur, [stageId]: [...existing, roleId] };
    });
  const [reassignShift, setReassignShift] = useState<StaffShiftWithAssignment | null>(null);
  // Target stage for duplicating the shift in the dialog (defaults to its own stage).
  const [dupStageId, setDupStageId] = useState<string>('');
  const [report, setReport] = useState<string | null>(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [confirmClearShifts, setConfirmClearShifts] = useState(false);

  // Lineup reveal flag (per-programme). While false, staff timetable links show
  // only shift info; flipping it on reveals the programmed acts on those links.
  const lineupRevealed = Boolean((program.settings as { lineup_revealed?: boolean } | null)?.lineup_revealed);
  const [linkCopied, setLinkCopied] = useState(false);

  // Staffing contact (who built the shifts), shown on staff-facing timetable
  // views so staff know who to reach. Persisted on the programme.
  const contactSettings = (program.settings as {
    staffing_contact_name?: string;
    staffing_contact_email?: string;
    staffing_contact_phone?: string;
  } | null) ?? {};
  const [contactName, setContactName] = useState(contactSettings.staffing_contact_name ?? '');
  const [contactEmail, setContactEmail] = useState(contactSettings.staffing_contact_email ?? '');
  const [contactPhone, setContactPhone] = useState(contactSettings.staffing_contact_phone ?? '');
  const [addShift, setAddShift] = useState<{ placeId: string; roleId: string; start: string; end: string } | null>(null);
  // Drag across a role lane to create a manual shift for that stage+role.
  const [laneDrag, setLaneDrag] = useState<{ stageId: string; roleId: string; a: number; b: number } | null>(null);
  const laneDragRef = useRef(laneDrag);
  laneDragRef.current = laneDrag;
  // Drag a shift bar's edge to resize it (15-min steps). Resizing converts a
  // generated shift to manual so the change survives regeneration.
  const [resize, setResize] = useState<{ shiftId: string; edge: 'start' | 'end'; origStart: number; origEnd: number; deltaMin: number } | null>(null);
  const resizeRef = useRef(resize);
  resizeRef.current = resize;
  const resizeStartXRef = useRef(0);
  const justResizedRef = useRef(false);

  const { data: shifts = [], isLoading } = useQuery({
    queryKey: ['program-shifts', programId],
    queryFn: () => listProgramShifts(programId),
  });

  const { data: roles = [] } = useQuery({
    queryKey: ['staff-roles'],
    queryFn: () => listStaffRoles(),
  });

  const { data: allPeople = [] } = useQuery({
    queryKey: ['people', { is_active: true }],
    queryFn: () => listPeople({ is_active: true }),
  });
  const staffPeople = useMemo(
    () => allPeople.filter((p) => p.type === 'staff' || p.type === 'support'),
    [allPeople],
  );

  const roleName = (id: string) => roles.find((r) => r.id === id)?.name ?? 'Role';
  const personName = (id: string) => staffPeople.find((p) => p.id === id)?.name ?? 'Unknown';

  const invalidateShifts = () => queryClient.invalidateQueries({ queryKey: ['program-shifts', programId] });

  // Persist staffing settings (shift length, max shifts) onto the program.
  const saveSettingsMutation = useMutation({
    mutationFn: (patch: Record<string, unknown>) =>
      updateProgram(programId, {
        settings: { ...((program.settings as Record<string, unknown>) ?? {}), ...patch },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['program', programId] }),
  });

  const copyTimetableLinkMutation = useMutation({
    mutationFn: async () => {
      const token = await ensureProgramTimetableToken(programId);
      const url = `${window.location.origin}/timetable/${program.slug}?token=${token}`;
      await navigator.clipboard.writeText(url);
    },
    onSuccess: () => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    },
    onError: (err) => setReport(`Could not copy link: ${err instanceof Error ? err.message : 'unknown error'}`),
  });

  const autoAssignMutation = useMutation({
    mutationFn: async () => {
      await saveSettingsMutation.mutateAsync({
        staffing_min_minutes: minMinutes,
        staffing_max_minutes: maxMinutes,
        staffing_max_shifts_per_person: maxShiftsPerPerson ?? null,
        staffing_leadin_minutes: leadInMinutes,
        staffing_leadout_minutes: leadOutMinutes,
      });
      await generateShifts(programId, { minShiftMinutes: minMinutes, maxShiftMinutes: maxMinutes, leadInMinutes, leadOutMinutes });
      return autoAssignStaff(programId, { maxShiftsPerPerson });
    },
    onSuccess: (res) => {
      invalidateShifts();
      setReport(
        `Assigned ${res.assigned} shift${res.assigned === 1 ? '' : 's'}` +
          (res.unfilled > 0 ? `, ${res.unfilled} unfilled` : '') +
          (res.warnings.length > 0 ? `, ${res.warnings.length} constraint warning${res.warnings.length === 1 ? '' : 's'}` : '') +
          '.',
      );
    },
    onError: (err) => setReport(`Auto-assign failed: ${err instanceof Error ? err.message : 'unknown error'}`),
  });

  const confirmMutation = useMutation({
    mutationFn: () => confirmAssignments(programId),
    onSuccess: () => {
      invalidateShifts();
      setReport('Assignments confirmed and locked. Reopen to let Auto-assign change them.');
    },
  });

  const reopenMutation = useMutation({
    mutationFn: () => reopenAssignments(programId),
    onSuccess: () => {
      invalidateShifts();
      setReport('All assignments reopened — unlocked and back to proposed.');
    },
  });

  const reopenOneMutation = useMutation({
    mutationFn: (shiftId: string) => reopenShiftAssignment(shiftId),
    onSuccess: () => {
      invalidateShifts();
      setReassignShift(null);
    },
  });

  const clearMutation = useMutation({
    mutationFn: (opts: { onlyUnlocked: boolean }) => clearProgramAssignments(programId, opts),
    onSuccess: (_data, vars) => {
      invalidateShifts();
      setConfirmClear(false);
      setReport(
        vars.onlyUnlocked
          ? 'Unlocked assignments cleared — locked & confirmed picks kept.'
          : 'All assignments cleared, including locked & confirmed picks.',
      );
    },
  });

  const clearShiftsMutation = useMutation({
    mutationFn: () => deleteProgramShifts(programId),
    onSuccess: () => {
      invalidateShifts();
      setConfirmClearShifts(false);
      setReport('All shifts deleted. Run Auto-assign to regenerate them.');
    },
  });

  const assignMutation = useMutation({
    mutationFn: ({ shiftId, personId }: { shiftId: string; personId: string }) =>
      assignPersonToShift(shiftId, personId, { lock: true }),
    onSuccess: () => {
      invalidateShifts();
      setReassignShift(null);
    },
  });

  const unassignMutation = useMutation({
    mutationFn: (shiftId: string) => unassignShift(shiftId),
    onSuccess: () => {
      invalidateShifts();
      setReassignShift(null);
    },
  });

  const createShiftMutation = useMutation({
    mutationFn: (input: { placeId: string; roleId: string; start: string; end: string }) =>
      createShift({
        program_id: programId,
        place_id: input.placeId,
        role_id: input.roleId,
        start_time: new Date(input.start).toISOString(),
        end_time: new Date(input.end).toISOString(),
        source: 'manual',
      }),
    onSuccess: () => {
      invalidateShifts();
      setAddShift(null);
      setReport('Manual shift added.');
    },
    onError: (err) => setReport(`Could not add shift: ${err instanceof Error ? err.message : 'unknown error'}`),
  });

  const deleteShiftMutation = useMutation({
    mutationFn: (shiftId: string) => deleteShift(shiftId),
    onSuccess: () => {
      invalidateShifts();
      setReassignShift(null);
    },
  });

  // Duplicate a shift's role + time onto a chosen stage (same one = parallel
  // coverage). The copy is an unassigned manual shift.
  const duplicateShiftMutation = useMutation({
    mutationFn: ({ shift, placeId }: { shift: StaffShiftWithAssignment; placeId: string }) =>
      createShift({
        program_id: programId,
        place_id: placeId,
        role_id: shift.role_id,
        start_time: shift.start_time,
        end_time: shift.end_time,
        source: 'manual',
      }),
    onSuccess: () => {
      invalidateShifts();
      setReassignShift(null);
      setReport('Shift duplicated.');
    },
    onError: (err) => setReport(`Could not duplicate shift: ${err instanceof Error ? err.message : 'unknown error'}`),
  });

  // Reset the duplicate target to the shift's own stage whenever the dialog opens.
  useEffect(() => {
    setDupStageId(reassignShift?.place_id ?? '');
  }, [reassignShift]);

  const resizeMutation = useMutation({
    mutationFn: (input: { shiftId: string; start: string; end: string }) =>
      // Always mark manual on resize so the edit survives regeneration.
      updateShift(input.shiftId, { start_time: input.start, end_time: input.end, source: 'manual' }),
    onSuccess: () => invalidateShifts(),
  });

  // Shifts across the whole programme, after filters. The timeline is
  // continuous, so no per-day scoping — a cross-midnight shift renders as one bar.
  const visibleShifts = useMemo(() => {
    return shifts.filter((s) => {
      if (roleFilter !== 'all' && s.role_id !== roleFilter) return false;
      if (personFilter !== 'all' && s.assignment?.person_id !== personFilter) return false;
      if (problemsOnly && s.assignment) return false; // problems = unassigned
      return true;
    });
  }, [shifts, roleFilter, personFilter, problemsOnly]);

  // Group by stage → role for sub-tracks.
  const shiftsByStageRole = useMemo(() => {
    const byStage = new Map<string, Map<string, StaffShiftWithAssignment[]>>();
    for (const s of visibleShifts) {
      const roleMap = byStage.get(s.place_id) ?? byStage.set(s.place_id, new Map()).get(s.place_id)!;
      (roleMap.get(s.role_id) ?? roleMap.set(s.role_id, []).get(s.role_id)!).push(s);
    }
    return byStage;
  }, [visibleShifts]);

  // Show every role's lane (per stage) for hand-building, either on request or
  // automatically while the programme has no shifts yet.
  const showAllLanes = manualBuild || shifts.length === 0;

  // Happenings grouped by stage — a reference row so you can see what each shift
  // is covering. The continuous timeline shows every day's acts at once.
  const happeningsByStage = useMemo(() => {
    const byStage = new Map<string, Happening[]>();
    for (const h of happenings) {
      if (!h.place_id || !h.start_time) continue;
      (byStage.get(h.place_id) ?? byStage.set(h.place_id, []).get(h.place_id)!).push(h);
    }
    return byStage;
  }, [happenings]);

  const minutesIntoTimeline = (iso: string) => Math.max(0, (Date.parse(iso) - timelineStart) / 60000);

  // "Now" line — shown when the current time falls inside the programme. Ticks
  // each minute so it stays roughly live without a heavy timer.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);
  const timelineEndMs = timelineStart + totalMinutes * 60000;
  const nowWithin = now >= timelineStart && now < timelineEndMs;
  const nowOffsetPx = ((now - timelineStart) / 60000 / SLOT_MINUTES) * SLOT_SIZE_PX;

  // Earliest scheduled time (shift or happening), so first open lands on content
  // rather than an empty midnight.
  const firstContentMs = useMemo(() => {
    let min = Infinity;
    for (const s of shifts) {
      const t = Date.parse(s.start_time);
      if (t < min) min = t;
    }
    for (const h of happenings) {
      if (!h.start_time) continue;
      const t = Date.parse(h.start_time);
      if (t < min) min = t;
    }
    return Number.isFinite(min) ? min : null;
  }, [shifts, happenings]);

  // On first open, scroll to "now" if the programme is running, otherwise to the
  // earliest scheduled item (with a little lead-in). Runs once, once the grid is
  // mounted and data has loaded.
  const initialScrollDoneRef = useRef(false);
  useEffect(() => {
    if (initialScrollDoneRef.current || isLoading) return;
    const el = gridScrollRef.current;
    if (!el) return;
    const target = nowWithin ? now : firstContentMs ?? timelineStart;
    const targetMin = Math.max(0, (target - timelineStart) / 60000);
    el.scrollTo({ left: Math.max(0, (targetMin / SLOT_MINUTES) * SLOT_SIZE_PX - SLOT_SIZE_PX) });
    initialScrollDoneRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, shifts, roles.length, stages.length, firstContentMs]);

  const laneStyle = (lane: number) => ({ top: lane * TRACK_HEIGHT + 3, height: TRACK_HEIGHT - 6 });

  const renderHappeningRef = (h: Happening, lane: number) => {
    if (!h.start_time) return null;
    const startMin = minutesIntoTimeline(h.start_time);
    const endMin = Math.min(totalMinutes, minutesIntoTimeline(h.end_time ?? h.start_time) || startMin + 60);
    const left = (startMin / SLOT_MINUTES) * SLOT_SIZE_PX;
    const width = Math.max(SLOT_SIZE_PX / 2, ((endMin - startMin) / SLOT_MINUTES) * SLOT_SIZE_PX);
    return (
      <div
        key={h.id}
        className="absolute overflow-hidden rounded border border-sky-500/40 bg-sky-500/10 px-2 text-xs text-sky-900 dark:text-sky-200"
        style={{ left, width, ...laneStyle(lane) }}
        title={h.title}
      >
        <div className="truncate font-medium">{h.title}</div>
      </div>
    );
  };

  const renderShiftBar = (s: StaffShiftWithAssignment, lane: number) => {
    // Apply live resize preview to the edge being dragged.
    let startMs = Date.parse(s.start_time);
    let endMs = Date.parse(s.end_time);
    if (resize?.shiftId === s.id) {
      if (resize.edge === 'start') startMs = resize.origStart + resize.deltaMin * 60000;
      else endMs = resize.origEnd + resize.deltaMin * 60000;
    }
    const startMin = Math.max(0, (startMs - timelineStart) / 60000);
    const endMin = Math.min(totalMinutes, (endMs - timelineStart) / 60000);
    const left = (startMin / SLOT_MINUTES) * SLOT_SIZE_PX;
    const width = Math.max(SLOT_SIZE_PX / 2, ((endMin - startMin) / SLOT_MINUTES) * SLOT_SIZE_PX);
    const assigned = !!s.assignment;
    const locked = !!s.assignment?.is_locked;
    const confirmed = s.assignment?.status === 'confirmed';
    const manual = s.source === 'manual';
    const SourceIcon = manual ? Pin : RefreshCw;
    const lockTitle = confirmed
      ? 'confirmed & locked — Auto-assign keeps this'
      : locked
        ? 'locked — Auto-assign keeps this'
        : 'proposed — Auto-assign may change this';
    return (
      <button
        key={s.id}
        type="button"
        onClick={() => { if (justResizedRef.current) return; setReassignShift(s); }}
        className={
          'group absolute overflow-hidden rounded border px-2 text-left text-xs ' +
          (!assigned
            ? 'border-destructive/60 bg-destructive/15 hover:bg-destructive/25'
            : confirmed
              ? 'border-emerald-600 bg-emerald-500/25 ring-1 ring-inset ring-emerald-500/40 hover:bg-emerald-500/30'
              : 'border-emerald-500/50 bg-emerald-500/15 hover:bg-emerald-500/25')
        }
        style={{ left, width, ...laneStyle(lane) }}
        title={`${roleName(s.role_id)} · ${fmtTime(new Date(startMs))}–${fmtTime(new Date(endMs))} · ${manual ? 'manual (kept on re-run)' : 'auto-generated (rebuilt on re-run)'}${assigned ? ` · ${lockTitle}` : ''}`}
      >
        {/* Resize handles */}
        <div
          onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); resizeStartXRef.current = e.clientX; setResize({ shiftId: s.id, edge: 'start', origStart: Date.parse(s.start_time), origEnd: Date.parse(s.end_time), deltaMin: 0 }); }}
          className="absolute inset-y-0 left-0 w-1.5 cursor-ew-resize opacity-0 hover:opacity-100 bg-foreground/30"
        />
        <div
          onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); resizeStartXRef.current = e.clientX; setResize({ shiftId: s.id, edge: 'end', origStart: Date.parse(s.start_time), origEnd: Date.parse(s.end_time), deltaMin: 0 }); }}
          className="absolute inset-y-0 right-0 w-1.5 cursor-ew-resize opacity-0 hover:opacity-100 bg-foreground/30"
        />
        <div className="flex items-center gap-1 truncate font-medium">
          <SourceIcon className="h-3 w-3 shrink-0 opacity-60" />
          <span className="truncate">{assigned ? s.person_name ?? personName(s.assignment!.person_id) : 'Unassigned'}</span>
          {locked && (
            <Lock
              className={'ml-auto h-3 w-3 shrink-0 ' + (confirmed ? 'text-emerald-600' : 'opacity-70')}
              aria-label={lockTitle}
            />
          )}
        </div>
        <div className="truncate text-[10px] text-muted-foreground">
          {fmtTime(new Date(startMs))}–{fmtTime(new Date(endMs))}
        </div>
      </button>
    );
  };

  const slots = useMemo(
    () => Array.from({ length: totalSlots }, (_, i) => new Date(timelineStart + i * SLOT_MINUTES * 60000)),
    [timelineStart, totalSlots],
  );

  // Finalize a lane drag (on mouse release anywhere) into a manual shift.
  useEffect(() => {
    const onUp = () => {
      const d = laneDragRef.current;
      if (!d) return;
      setLaneDrag(null);
      const lo = Math.min(d.a, d.b);
      const hi = Math.max(d.a, d.b);
      const start = slots[lo];
      const end = slots[hi] ? new Date(slots[hi].getTime() + SLOT_MINUTES * 60000) : undefined;
      if (!start || !end) return;
      createShiftMutation.mutate({
        placeId: d.stageId,
        roleId: d.roleId,
        start: start.toISOString(),
        end: end.toISOString(),
      });
    };
    window.addEventListener('mouseup', onUp);
    return () => window.removeEventListener('mouseup', onUp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slots]);

  const inLaneDrag = (stageId: string, roleId: string, idx: number) =>
    !!laneDrag && laneDrag.stageId === stageId && laneDrag.roleId === roleId &&
    idx >= Math.min(laneDrag.a, laneDrag.b) && idx <= Math.max(laneDrag.a, laneDrag.b);

  // Live resize: track the dragged edge and commit on release.
  const RESIZE_SNAP_MIN = 15;
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const r = resizeRef.current;
      if (!r) return;
      const dx = e.clientX - resizeStartXRef.current;
      const deltaMin = Math.round((dx / SLOT_SIZE_PX) * SLOT_MINUTES / RESIZE_SNAP_MIN) * RESIZE_SNAP_MIN;
      if (deltaMin !== r.deltaMin) setResize({ ...r, deltaMin });
    };
    const onUp = () => {
      const r = resizeRef.current;
      if (!r) return;
      setResize(null);
      if (r.deltaMin === 0) return;
      justResizedRef.current = true;
      setTimeout(() => { justResizedRef.current = false; }, 0);
      const minMs = RESIZE_SNAP_MIN * 60000;
      let start = r.origStart;
      let end = r.origEnd;
      if (r.edge === 'start') start = Math.min(r.origStart + r.deltaMin * 60000, r.origEnd - minMs);
      else end = Math.max(r.origEnd + r.deltaMin * 60000, r.origStart + minMs);
      resizeMutation.mutate({ shiftId: r.shiftId, start: new Date(start).toISOString(), end: new Date(end).toISOString() });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Candidate people for the reassign dialog: role-qualified first.
  const reassignCandidates = useMemo(() => {
    if (!reassignShift) return [];
    return [...staffPeople].sort((a, b) => a.name?.localeCompare(b.name ?? '') ?? 0);
  }, [reassignShift, staffPeople]);

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={() => autoAssignMutation.mutate()} disabled={autoAssignMutation.isPending}>
          <Wand2 className="mr-2 h-4 w-4" />
          {autoAssignMutation.isPending ? 'Assigning…' : 'Auto-assign'}
        </Button>
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          Shift length
          <input
            type="number"
            min={0.25}
            step={0.25}
            value={minHours}
            onChange={(e) => setMinHours(e.target.value)}
            onBlur={() => saveSettingsMutation.mutate({ staffing_min_minutes: minMinutes })}
            className="w-16 rounded-md border border-input bg-background px-2 py-1.5 text-sm"
            title="Minimum shift length in hours. Coverage runs are balanced so shifts stay at or above this (a run shorter than the max stays one shift)."
          />
          –
          <input
            type="number"
            min={0.25}
            step={0.25}
            value={maxHours}
            onChange={(e) => setMaxHours(e.target.value)}
            onBlur={() => saveSettingsMutation.mutate({ staffing_max_minutes: maxMinutes })}
            className="w-16 rounded-md border border-input bg-background px-2 py-1.5 text-sm"
            title="Maximum shift length in hours. Runs are split so no shift exceeds this; the average lands near the middle of the band."
          />
          h
        </label>
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          Max shifts / person
          <input
            type="number"
            min={1}
            step={1}
            value={maxShiftsInput}
            placeholder="∞"
            onChange={(e) => setMaxShiftsInput(e.target.value)}
            onBlur={() => saveSettingsMutation.mutate({ staffing_max_shifts_per_person: maxShiftsPerPerson ?? null })}
            className="w-20 rounded-md border border-input bg-background px-2 py-1.5 text-sm"
            title="Cap on how many shifts any one person can be assigned. Leave blank for no cap."
          />
        </label>
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          Lead-in
          <input
            type="number"
            min={0}
            step={15}
            value={leadInInput}
            placeholder="0"
            onChange={(e) => setLeadInInput(e.target.value)}
            onBlur={() => saveSettingsMutation.mutate({ staffing_leadin_minutes: leadInMinutes })}
            className="w-16 rounded-md border border-input bg-background px-2 py-1.5 text-sm"
            title="Minutes of coverage before the first act of a run (call-time)."
          />
          min
        </label>
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          Lead-out
          <input
            type="number"
            min={0}
            step={15}
            value={leadOutInput}
            placeholder="0"
            onChange={(e) => setLeadOutInput(e.target.value)}
            onBlur={() => saveSettingsMutation.mutate({ staffing_leadout_minutes: leadOutMinutes })}
            className="w-16 rounded-md border border-input bg-background px-2 py-1.5 text-sm"
            title="Minutes of coverage after the last act of a run (teardown)."
          />
          min
        </label>
        <Button
          variant="outline"
          onClick={() => {
            const pad = (n: number) => String(n).padStart(2, '0');
            const dayStr = `${activeDay.getFullYear()}-${pad(activeDay.getMonth() + 1)}-${pad(activeDay.getDate())}`;
            setAddShift({
              placeId: stages[0]?.id ?? '',
              roleId: roles[0]?.id ?? '',
              start: `${dayStr}T18:00`,
              end: `${dayStr}T22:00`,
            });
          }}
          disabled={stages.length === 0 || roles.length === 0}
          title="Add a manual shift (e.g. call-time before the first act). Survives Auto-assign."
        >
          <Plus className="mr-2 h-4 w-4" />
          Add shift
        </Button>
        <Button
          variant="outline"
          onClick={() => confirmMutation.mutate()}
          disabled={confirmMutation.isPending || shifts.every((s) => !s.assignment)}
          title="Confirm and lock every assignment so Auto-assign won't change them"
        >
          <CheckCheck className="mr-2 h-4 w-4" />
          Confirm all
        </Button>
        <Button
          variant="outline"
          onClick={() => reopenMutation.mutate()}
          disabled={
            reopenMutation.isPending ||
            !shifts.some((s) => s.assignment && (s.assignment.is_locked || s.assignment.status === 'confirmed'))
          }
          title="Unlock every assignment and set it back to proposed so Auto-assign can change it"
        >
          <Unlock className="mr-2 h-4 w-4" />
          Reopen all
        </Button>
        <Button
          variant="outline"
          className="text-destructive"
          onClick={() => setConfirmClear(true)}
          disabled={clearMutation.isPending || shifts.every((s) => !s.assignment)}
          title="Remove every person from shifts (keeps the shifts)"
        >
          <Eraser className="mr-2 h-4 w-4" />
          Clear assignments
        </Button>
        <Button
          variant="outline"
          className="text-destructive"
          onClick={() => setConfirmClearShifts(true)}
          disabled={clearShiftsMutation.isPending || shifts.length === 0}
          title="Delete all shifts and their assignments (Auto-assign rebuilds them)"
        >
          <Trash2 className="mr-2 h-4 w-4" />
          Clear shifts
        </Button>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
          >
            <option value="all">All roles</option>
            {roles.map((r) => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
          <select
            value={personFilter}
            onChange={(e) => setPersonFilter(e.target.value)}
            className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
          >
            <option value="all">All people</option>
            {staffPeople.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <label className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
            <input
              type="checkbox"
              checked={problemsOnly}
              onChange={(e) => setProblemsOnly(e.target.checked)}
            />
            Problems only
          </label>
          <label
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground"
            title="Show a draggable lane for every role on every stage so you can build shifts by hand without Auto-assign."
          >
            <input
              type="checkbox"
              checked={manualBuild}
              onChange={(e) => setManualBuild(e.target.checked)}
            />
            Manual build
          </label>
        </div>
      </div>

      {report && (
        <div className="rounded-md border border-input bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
          {report}
        </div>
      )}

      {/* Staff timetable sharing: reveal toggle + global read-only link */}
      <div className="flex flex-wrap items-center gap-3 rounded-md border border-input bg-muted/20 px-3 py-2">
        <span className="text-sm font-medium">Staff timetable</span>
        <Button
          variant={lineupRevealed ? 'default' : 'outline'}
          size="sm"
          onClick={() => saveSettingsMutation.mutate({ lineup_revealed: !lineupRevealed })}
          disabled={saveSettingsMutation.isPending}
          title={
            lineupRevealed
              ? 'Line-up is visible on staff timetable links. Click to hide it again.'
              : 'Line-up is hidden from staff timetable links. Click to reveal the programmed acts.'
          }
        >
          {lineupRevealed ? <Eye className="mr-2 h-4 w-4" /> : <EyeOff className="mr-2 h-4 w-4" />}
          {lineupRevealed ? 'Line-up revealed' : 'Line-up secret'}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => copyTimetableLinkMutation.mutate()}
          disabled={copyTimetableLinkMutation.isPending}
          title="Copy a read-only timetable link (all stages and staff) to share with the whole team."
        >
          <Link2 className="mr-2 h-4 w-4" />
          {linkCopied ? 'Copied!' : 'Copy global link'}
        </Button>
        <span className="text-xs text-muted-foreground">
          {lineupRevealed
            ? 'Staff links show the programmed acts alongside shifts.'
            : 'Staff links show shifts only — the line-up stays hidden.'}
        </span>
        <div className="flex w-full flex-wrap items-center gap-2 border-t border-border/40 pt-2">
          <span className="text-xs text-muted-foreground">Shifts contact</span>
          <input
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            onBlur={() => saveSettingsMutation.mutate({ staffing_contact_name: contactName.trim() })}
            placeholder="Name"
            className="w-36 rounded-md border border-input bg-background px-2 py-1.5 text-sm"
            title="Who put the shifts together — shown to staff on their timetable so they know who to contact."
          />
          <input
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            onBlur={() => saveSettingsMutation.mutate({ staffing_contact_email: contactEmail.trim() })}
            placeholder="Email"
            className="w-52 rounded-md border border-input bg-background px-2 py-1.5 text-sm"
          />
          <input
            value={contactPhone}
            onChange={(e) => setContactPhone(e.target.value)}
            onBlur={() => saveSettingsMutation.mutate({ staffing_contact_phone: contactPhone.trim() })}
            placeholder="Phone"
            className="w-40 rounded-md border border-input bg-background px-2 py-1.5 text-sm"
          />
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <RefreshCw className="h-3 w-3" /> auto-generated (rebuilt on Auto-assign)
        </span>
        <span className="inline-flex items-center gap-1">
          <Pin className="h-3 w-3" /> manual (kept on Auto-assign)
        </span>
        <span className="inline-flex items-center gap-1">
          <Lock className="h-3 w-3 text-emerald-600" /> locked / confirmed (Auto-assign keeps it)
        </span>
        <span>Drag an empty lane to add a shift · drag a shift's edge to resize (becomes manual)</span>
      </div>

      {/* Day pills — jump the continuous timeline to a day. */}
      {days.length > 1 && (
        <div className="flex flex-wrap gap-2">
          {days.map((d, idx) => (
            <Button
              key={idx}
              size="sm"
              variant={idx === activeDayIdx ? 'default' : 'outline'}
              onClick={() => scrollToDay(idx)}
            >
              {d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}
            </Button>
          ))}
        </div>
      )}

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading shifts…</p>
      ) : stages.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Add stages to this programme (via <strong>Edit program</strong>) before building shifts.
        </p>
      ) : roles.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Create staff roles first, then drag on a lane or use <strong>Add shift</strong> to build the timetable.
        </p>
      ) : (
        <>
        {shifts.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No shifts yet — drag across a role lane below (or use <strong>Add shift</strong>) to build them by hand, or
            click <strong>Auto-assign</strong> to generate them from happening requirements.
          </p>
        )}
        <div ref={gridScrollRef} onScroll={onGridScroll} className="overflow-auto rounded-md border" style={{ maxHeight: '70vh' }}>
          <div style={{ minWidth: LABEL_WIDTH + slots.length * SLOT_SIZE_PX }}>
            {/* Time axis: a day band over a row of half-hour labels */}
            <div className="sticky top-0 z-20 border-b bg-muted/95 text-xs text-muted-foreground backdrop-blur">
              {/* Day band — marks where each festival day starts on the continuous axis */}
              <div className="flex border-b border-border/40">
                <div className="sticky left-0 z-10 shrink-0 border-r bg-muted/95" style={{ width: LABEL_WIDTH }} />
                <div className="flex">
                  {days.map((d, idx) => (
                    <div
                      key={idx}
                      style={{ width: dayWidthPx }}
                      className="shrink-0 border-l-2 border-foreground/30 px-2 py-1 font-medium first:border-l-0"
                    >
                      {d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}
                    </div>
                  ))}
                </div>
              </div>
              {/* Half-hour labels */}
              <div className="flex">
                <div className="sticky left-0 z-10 shrink-0 border-r bg-muted/95 p-2 font-medium" style={{ width: LABEL_WIDTH }}>
                  Stage / role
                </div>
                <div className="flex">
                  {slots.map((slot, idx) => (
                    <div
                      key={idx}
                      style={{ width: SLOT_SIZE_PX }}
                      className={
                        'shrink-0 border-r p-1 text-center ' +
                        (slot.getHours() === 0 && slot.getMinutes() === 0 ? 'border-l-2 border-foreground/30' : '')
                      }
                    >
                      {slot.getMinutes() === 0 ? fmtTime(slot) : ''}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Stage rows: a header, a happenings reference lane, then a lane per role */}
            {stages.map((stage) => {
              const roleMap = shiftsByStageRole.get(stage.id);
              const shiftRoleIds = roleMap ? Array.from(roleMap.keys()) : [];
              // In manual-build mode show every role's lane so any can be dragged
              // on; otherwise show lanes that have shifts plus any the user has
              // explicitly revealed on this stage.
              const roleIds = showAllLanes
                ? Array.from(new Set([...roles.map((r) => r.id), ...shiftRoleIds]))
                : Array.from(new Set([...shiftRoleIds, ...(revealedLanes[stage.id] ?? [])]));
              const stageHappenings = happeningsByStage.get(stage.id) ?? [];
              const trackWidth = slots.length * SLOT_SIZE_PX;
              // Pack overlapping items into stacked sub-lanes so they don't overlap.
              const happeningsPack = packLanes(stageHappenings);
              const rolePacks = new Map(roleIds.map((rid) => [rid, packLanes(roleMap?.get(rid) ?? [])]));
              const laneHeight = (count: number) => count * TRACK_HEIGHT;
              return (
                <div key={stage.id} className="flex border-b last:border-b-0">
                  {/* Label column */}
                  <div className="sticky left-0 z-10 shrink-0 border-r bg-background" style={{ width: LABEL_WIDTH }}>
                    <div className="border-b p-2 text-sm font-medium" style={{ height: STAGE_HEADER_HEIGHT }}>
                      {stage.name}
                    </div>
                    <div className="flex items-center border-b border-border/30 px-2 text-xs italic text-muted-foreground" style={{ height: laneHeight(happeningsPack.count) }}>
                      Happenings
                    </div>
                    {roleIds.length === 0 ? (
                      <div className="flex items-center px-2" style={{ height: TRACK_HEIGHT }}>
                        <select
                          value=""
                          onChange={(e) => { if (e.target.value) revealLane(stage.id, e.target.value); }}
                          className="w-full rounded-md border border-input bg-background px-1.5 py-1 text-xs text-muted-foreground"
                          title="Pick a role to add a draggable lane for this stage, then drag to create a shift."
                        >
                          <option value="">+ Add shift…</option>
                          {roles.map((r) => (
                            <option key={r.id} value={r.id}>{r.name}</option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      roleIds.map((rid) => (
                        <div key={rid} className="flex items-center border-b border-border/30 px-2 text-xs text-muted-foreground last:border-b-0" style={{ height: laneHeight(rolePacks.get(rid)!.count) }}>
                          {roleName(rid)}
                        </div>
                      ))
                    )}
                  </div>
                  {/* Tracks column */}
                  <div className="relative" style={{ width: trackWidth }}>
                    {/* Day-boundary divider lines down the whole stage block */}
                    {Array.from({ length: dayCount - 1 }, (_, i) => (
                      <div
                        key={`day-divider-${i}`}
                        className="pointer-events-none absolute inset-y-0 z-0 border-l-2 border-foreground/20"
                        style={{ left: (i + 1) * dayWidthPx }}
                      />
                    ))}
                    {/* "Now" line */}
                    {nowWithin && (
                      <div
                        className="pointer-events-none absolute inset-y-0 z-10 border-l-2 border-red-500"
                        style={{ left: nowOffsetPx }}
                      />
                    )}
                    <div className="border-b" style={{ height: STAGE_HEADER_HEIGHT }} />
                    <div className="relative border-b border-border/30" style={{ height: laneHeight(happeningsPack.count) }}>
                      {stageHappenings.map((h) => renderHappeningRef(h, happeningsPack.lanes.get(h) ?? 0))}
                    </div>
                    {roleIds.length === 0 ? (
                      <div style={{ height: TRACK_HEIGHT }} />
                    ) : (
                      roleIds.map((rid) => {
                        const pack = rolePacks.get(rid)!;
                        return (
                          <div key={rid} className="relative border-b border-border/30 last:border-b-0" style={{ height: laneHeight(pack.count) }}>
                            {/* Drag-to-create overlay: drag across empty slots to add a manual shift */}
                            <div className="absolute inset-0 flex">
                              {slots.map((slot, idx) => (
                                <div
                                  key={idx}
                                  onMouseDown={(e) => { e.preventDefault(); setLaneDrag({ stageId: stage.id, roleId: rid, a: idx, b: idx }); }}
                                  onMouseEnter={() => setLaneDrag((s) => (s && s.stageId === stage.id && s.roleId === rid ? { ...s, b: idx } : s))}
                                  style={{ width: SLOT_SIZE_PX }}
                                  className={`shrink-0 border-r border-border/20 ${inLaneDrag(stage.id, rid, idx) ? 'bg-primary/20' : 'hover:bg-muted/30'}`}
                                  aria-label={`Add ${roleName(rid)} shift at ${fmtTime(slot)}`}
                                />
                              ))}
                            </div>
                            {(roleMap?.get(rid) ?? []).map((s) => renderShiftBar(s, pack.lanes.get(s) ?? 0))}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        </>
      )}

      {/* Clear-all confirmation */}
      <Dialog open={confirmClear} onOpenChange={(open) => !open && setConfirmClear(false)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Clear assignments?
            </DialogTitle>
            <DialogDescription>
              The shifts themselves are kept, so you can re-run Auto-assign afterwards. This can't be undone.
              Locked &amp; confirmed picks (manual assignments and confirmed rosters) are protected unless
              you choose to wipe everything.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <Button
              variant="outline"
              onClick={() => clearMutation.mutate({ onlyUnlocked: true })}
              disabled={clearMutation.isPending}
            >
              <Unlock className="mr-2 h-4 w-4" />
              Clear unlocked only (keep locked &amp; confirmed)
            </Button>
            <Button
              variant="destructive"
              onClick={() => clearMutation.mutate({ onlyUnlocked: false })}
              disabled={clearMutation.isPending}
            >
              <Eraser className="mr-2 h-4 w-4" />
              Clear everything, including locked
            </Button>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmClear(false)} disabled={clearMutation.isPending}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clear-shifts confirmation */}
      <Dialog open={confirmClearShifts} onOpenChange={(open) => !open && setConfirmClearShifts(false)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Delete all shifts?
            </DialogTitle>
            <DialogDescription>
              This deletes every shift for this program and all their assignments (including manual,
              locked picks). Use this to rebuild from scratch — run Auto-assign afterwards to
              regenerate shifts from the current happening times. This can't be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmClearShifts(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => clearShiftsMutation.mutate()}
              disabled={clearShiftsMutation.isPending}
            >
              {clearShiftsMutation.isPending ? 'Deleting…' : 'Delete all shifts'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add manual shift dialog */}
      <Dialog open={!!addShift} onOpenChange={(open) => !open && setAddShift(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add shift</DialogTitle>
            <DialogDescription>
              A manual shift (e.g. call-time before the first act, or standalone coverage). It's kept
              when you re-run Auto-assign and can be assigned like any other shift.
            </DialogDescription>
          </DialogHeader>
          {addShift && (
            <div className="space-y-3 px-4 sm:px-6 py-4">
              <div className="space-y-1">
                <label className="text-sm font-medium">Stage</label>
                <select
                  value={addShift.placeId}
                  onChange={(e) => setAddShift({ ...addShift, placeId: e.target.value })}
                  className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm"
                >
                  {stages.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Role</label>
                <select
                  value={addShift.roleId}
                  onChange={(e) => setAddShift({ ...addShift, roleId: e.target.value })}
                  className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm"
                >
                  {roles.map((r) => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-sm font-medium">Start</label>
                  <input
                    type="datetime-local"
                    value={addShift.start}
                    onChange={(e) => setAddShift({ ...addShift, start: e.target.value })}
                    className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">End</label>
                  <input
                    type="datetime-local"
                    value={addShift.end}
                    onChange={(e) => setAddShift({ ...addShift, end: e.target.value })}
                    className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm"
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddShift(null)}>Cancel</Button>
            <Button
              onClick={() => addShift && createShiftMutation.mutate(addShift)}
              disabled={
                createShiftMutation.isPending ||
                !addShift?.placeId ||
                !addShift?.roleId ||
                !addShift?.start ||
                !addShift?.end ||
                Date.parse(addShift?.end ?? '') <= Date.parse(addShift?.start ?? '')
              }
            >
              {createShiftMutation.isPending ? 'Adding…' : 'Add shift'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reassign dialog */}
      <Dialog open={!!reassignShift} onOpenChange={(open) => !open && setReassignShift(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {reassignShift && `${roleName(reassignShift.role_id)} shift`}
            </DialogTitle>
            <DialogDescription>
              {reassignShift && (
                <>
                  {fmtTime(new Date(reassignShift.start_time))}–{fmtTime(new Date(reassignShift.end_time))}
                  {reassignShift.assignment && (
                    <> · currently {reassignShift.person_name ?? personName(reassignShift.assignment.person_id)}</>
                  )}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-72 space-y-1 overflow-y-auto px-4 sm:px-6 py-2">
            {reassignCandidates.length === 0 ? (
              <p className="text-sm text-muted-foreground">No staff/support people available.</p>
            ) : (
              reassignCandidates.map((p) => {
                const isCurrent = reassignShift?.assignment?.person_id === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    disabled={assignMutation.isPending}
                    onClick={() => reassignShift && assignMutation.mutate({ shiftId: reassignShift.id, personId: p.id })}
                    className={
                      'flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-sm ' +
                      (isCurrent ? 'border-primary bg-primary/10' : 'border-input hover:bg-accent')
                    }
                  >
                    <span>{p.name}</span>
                    {isCurrent && <span className="text-xs text-muted-foreground">current</span>}
                  </button>
                );
              })
            )}
          </div>
          {reassignShift && (
            <div className="border-t px-4 sm:px-6 py-3">
              <label className="text-xs font-medium text-muted-foreground">Duplicate this shift to</label>
              <div className="mt-1 flex items-center gap-2">
                <select
                  value={dupStageId}
                  onChange={(e) => setDupStageId(e.target.value)}
                  className="flex-1 rounded-md border border-input bg-background px-2 py-1.5 text-sm"
                >
                  {stages.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                      {s.id === reassignShift.place_id ? ' (same stage)' : ''}
                    </option>
                  ))}
                </select>
                <Button
                  variant="outline"
                  onClick={() =>
                    duplicateShiftMutation.mutate({
                      shift: reassignShift,
                      placeId: dupStageId || reassignShift.place_id,
                    })
                  }
                  disabled={duplicateShiftMutation.isPending || stages.length === 0}
                >
                  <Copy className="mr-2 h-4 w-4" />
                  Duplicate
                </Button>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Creates an unassigned manual shift with the same role and time.
              </p>
            </div>
          )}
          <DialogFooter className="flex-col items-stretch gap-3 sm:flex-col sm:items-stretch">
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
              <AlertTriangle className="h-3 w-3 shrink-0" />
              Manual picks are locked and survive re-runs.
            </span>
            <div className="flex flex-wrap justify-end gap-2">
              {reassignShift?.assignment &&
                (reassignShift.assignment.is_locked || reassignShift.assignment.status === 'confirmed') && (
                  <Button
                    variant="outline"
                    onClick={() => reassignShift && reopenOneMutation.mutate(reassignShift.id)}
                    disabled={reopenOneMutation.isPending}
                    title="Unlock and set back to proposed so Auto-assign can reassign it"
                  >
                    <Unlock className="mr-2 h-4 w-4" />
                    Reopen
                  </Button>
                )}
              {reassignShift?.assignment && (
                <Button
                  variant="outline"
                  onClick={() => reassignShift && unassignMutation.mutate(reassignShift.id)}
                  disabled={unassignMutation.isPending}
                >
                  Unassign
                </Button>
              )}
              {reassignShift?.source === 'manual' && (
                <Button
                  variant="outline"
                  className="text-destructive"
                  onClick={() => reassignShift && deleteShiftMutation.mutate(reassignShift.id)}
                  disabled={deleteShiftMutation.isPending}
                  title="Delete this manual shift"
                >
                  Delete shift
                </Button>
              )}
              <Button variant="outline" onClick={() => setReassignShift(null)}>
                Close
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
